const UTM_STORAGE_KEY = 'seeds_utm_params';

export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_description',
] as const;

export type UtmParamKey = (typeof UTM_PARAM_KEYS)[number];
export type UtmParams = Partial<Record<UtmParamKey, string>>;

function parseUtmFromSearch(search: string): UtmParams {
  const params = new URLSearchParams(search);
  const result: UtmParams = {};

  for (const key of UTM_PARAM_KEYS) {
    const value = params.get(key);
    if (value) {
      result[key] = value;
    }
  }

  return result;
}

function readStoredUtmParams(): UtmParams {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    const result: UtmParams = {};
    for (const key of UTM_PARAM_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value) {
        result[key] = value;
      }
    }

    return result;
  } catch {
    return {};
  }
}

function writeStoredUtmParams(params: UtmParams): void {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
}

/** Reads UTM params from the current URL and merges them into sessionStorage. */
export function captureUtmFromUrl(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): void {
  const incoming = parseUtmFromSearch(search);
  if (Object.keys(incoming).length === 0) {
    return;
  }

  writeStoredUtmParams({
    ...readStoredUtmParams(),
    ...incoming,
  });
}

/** Returns UTM params persisted in sessionStorage (empty object when none stored). */
export function getUtmParams(): UtmParams {
  return readStoredUtmParams();
}
